import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Calendar, Briefcase, Heart, Repeat2, MessageCircle, AlertOctagon, TrendingUp, Twitter } from "lucide-react";

/**
 * Political Dossier panel: curated bio + recent X posts + reputational risk events.
 * Displays real data scraped from public sources.
 */

const TOPIC_COLORS = {
  energia: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "energia-nuclear": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  tecnologia: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  diplomacia: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  territorio: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  inversion: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};

const SEVERITY_STYLE = (s) =>
  s >= 8
    ? { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-300", label: "Critico" }
    : s >= 6
    ? { bg: "bg-orange-500/10", border: "border-orange-500/30", text: "text-orange-300", label: "Alto" }
    : { bg: "bg-yellow-500/10", border: "border-yellow-500/30", text: "text-yellow-300", label: "Medio" };

const BioCard = ({ bio }) => {
  if (!bio) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6"
      data-testid="dossier-bio"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-purple-400 font-medium">Perfil curado</span>
        <span className="text-[9px] uppercase tracking-widest text-emerald-400">verificado</span>
      </div>
      <h3 className="text-lg font-bold text-white leading-tight">{bio.full_name}</h3>
      <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">{bio.role_detail}</p>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-xs text-gray-300">{bio.age} anos</span>
        </div>
        <div className="flex items-center gap-2">
          <GraduationCap className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-xs text-gray-300">{bio.education?.length || 0} titulos</span>
        </div>
      </div>

      {bio.education?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Formacion</p>
          <ul className="space-y-1">
            {bio.education.map((e, i) => (
              <li key={`edu-${e?.slice(0, 30) || i}`} className="text-[11px] text-gray-300 leading-relaxed flex items-start gap-2">
                <span className="text-purple-400 mt-1">·</span>
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {bio.highlights?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Highlights</p>
          <ul className="space-y-1.5">
            {bio.highlights.map((h, i) => (
              <li key={`hl-${h?.slice(0, 30) || i}`} className="text-[11px] text-gray-300 leading-relaxed flex items-start gap-2">
                <Briefcase className="w-3 h-3 text-purple-400/70 mt-0.5 flex-shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
};

const PostCard = ({ post }) => {
  const topicColor = TOPIC_COLORS[post.topic] || "bg-white/5 text-gray-300 border-white/10";
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      className="p-4 rounded-xl bg-[#0A0A0F] border border-white/[0.06] hover:border-purple-500/20 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Twitter className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-[10px] uppercase tracking-widest text-gray-500">{post.date}</span>
        </div>
        <span className={`text-[9px] uppercase tracking-widest font-medium px-2 py-0.5 rounded border ${topicColor}`}>{post.topic}</span>
      </div>
      <p className="text-xs text-gray-300 leading-relaxed mb-3">{post.text}</p>
      <div className="flex items-center gap-4 text-[11px]">
        <span className="flex items-center gap-1 text-pink-300/80"><Heart className="w-3 h-3" />{post.likes}</span>
        <span className="flex items-center gap-1 text-emerald-300/80"><Repeat2 className="w-3 h-3" />{post.reposts}</span>
        <span className="flex items-center gap-1 text-blue-300/80"><MessageCircle className="w-3 h-3" />{post.replies}</span>
        <span className="ml-auto text-[10px] text-gray-500 font-mono">engagement: {post.likes + post.reposts * 2 + post.replies}</span>
      </div>
    </motion.div>
  );
};

const PostsCard = ({ posts, engagement }) => {
  if (!posts?.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6"
      data-testid="dossier-posts"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-cyan-400 font-medium">Actividad X · Posts recientes</span>
          <h3 className="text-sm font-bold text-white mt-1">{posts.length} publicaciones analizadas</h3>
        </div>
        {engagement && (
          <div className="text-right">
            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Engagement promedio</p>
            <p className="text-lg font-bold text-white">{engagement.avg_likes} <span className="text-[10px] text-gray-500">likes</span></p>
          </div>
        )}
      </div>
      <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1 custom-scroll">
        {posts.map((p, i) => <PostCard key={`post-${p.id || p.url || i}`} post={p} />)}
      </div>
      {engagement?.observation && (
        <div className="mt-4 pt-4 border-t border-white/5 flex items-start gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-gray-400 leading-relaxed italic">{engagement.observation}</p>
        </div>
      )}
    </motion.div>
  );
};

const RiskEventCard = ({ event }) => {
  const style = SEVERITY_STYLE(event.severity);
  return (
    <motion.div
      initial={{ opacity: 0, x: 6 }}
      animate={{ opacity: 1, x: 0 }}
      className={`p-4 rounded-xl border ${style.bg} ${style.border}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2 flex-1">
          <AlertOctagon className={`w-4 h-4 ${style.text} mt-0.5 flex-shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white leading-tight">{event.event}</p>
            <span className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5 block">{event.date} · {event.category}</span>
          </div>
        </div>
        <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-1 rounded ${style.bg} ${style.text} border ${style.border} flex-shrink-0`}>
          {style.label} {event.severity}/10
        </span>
      </div>
      <p className="text-[11px] text-gray-300 leading-relaxed mt-2">{event.description}</p>
    </motion.div>
  );
};

const RiskEventsCard = ({ events }) => {
  if (!events?.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6"
      data-testid="dossier-risks"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-widest text-red-400 font-medium">Historico de riesgo reputacional</span>
        <span className="text-[9px] uppercase tracking-widest text-gray-500">{events.length} eventos</span>
      </div>
      <div className="space-y-3">
        {events.map((e, i) => <RiskEventCard key={`event-${e.id || e.title || i}`} event={e} />)}
      </div>
    </motion.div>
  );
};

export const PoliticalDossier = ({ dossier }) => {
  if (!dossier) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="political-dossier">
      <BioCard bio={dossier.bio_enrichment} />
      <PostsCard posts={dossier.recent_posts} engagement={dossier.engagement_metrics} />
      <RiskEventsCard events={dossier.risk_events} />
    </div>
  );
};

export default PoliticalDossier;
